import React, { useEffect, useState } from "react";
import { Facebook, Instagram, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

/**
 * Sidebar connection pills.
 *
 * Customer-friendly only: shows a glanceable status and links to the full
 * Connections page. No "demo", no setup jargon, no inline dialogs.
 */
export default function ConnectionPill() {
  const [platforms, setPlatforms] = useState([]);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const res = await api.get("/connections");
      setPlatforms(res.data?.platforms || []);
    } catch (_e) {
      setPlatforms([]);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const fb = platforms.find((p) => p.platform === "Facebook");
  const ig = platforms.find((p) => p.platform === "Instagram");

  const Row = ({ icon: Icon, label, p, testId }) => {
    const isConnected = !!p?.connected;
    const isUnavailable = p && !p.connected && !p.coming_soon && !p.configured;
    const StatusIcon = isConnected ? CheckCircle2 : isUnavailable ? AlertCircle : Clock;
    const statusColor = isConnected
      ? "text-[color:var(--success)]"
      : isUnavailable
      ? "text-[color:var(--accent-warm)]"
      : "text-[color:var(--text-3)]";

    // Strip "· Demo" suffix from display name (customer-friendly)
    const cleanName = p?.selected?.name?.replace(/\s*·\s*Demo\s*$/i, "") || p?.selected?.name;
    
    const tagline = isConnected
      ? cleanName
        ? `Posting to "${cleanName}"`
        : "Connected"
      : isUnavailable
      ? "Temporarily unavailable"
      : "Tap to connect";

    return (
      <button
        type="button"
        onClick={() => navigate("/settings/connections")}
        data-testid={testId}
        className="w-full text-left rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] hover:bg-[color:var(--surface-2)] px-3 py-3 flex items-start gap-3 transition-colors"
      >
        <div
          className="h-8 w-8 rounded-md flex items-center justify-center"
          style={{ background: `${p?.color || "#888"}25`, color: p?.color || "var(--text-1)" }}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm text-[color:var(--text-1)] font-medium">
            {label}
            <StatusIcon className={"h-3.5 w-3.5 " + statusColor} />
          </div>
          <div className="text-xs text-[color:var(--text-3)] truncate">{tagline}</div>
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-2">
      <Row icon={Facebook} label="Facebook Page" p={fb} testId="sidebar-facebook-connection" />
      <Row icon={Instagram} label="Instagram" p={ig} testId="sidebar-instagram-connection" />
    </div>
  );
}
